import type { Command } from "@/context/WebContainerContext";
import { createId } from "@paralleldrive/cuid2";
import { entries } from "lodash";
import { DiNodejsSmall, DiPython } from "react-icons/di";
import type { IconType } from "react-icons/lib";

export enum TemplateType {
	JavaScript = "javascript",
	Python = "python",
}

export interface Template {
	/* The name of the template */
	name: string;
	/* The type of the template */
	type: TemplateType;
	/* The logo of the template */
	logo: IconType;
	/* The base64 encoded value of the template */
	value: string;
	/* The description of the template */
	description?: string;
	/* The default open file/s for the template */
	defaultOpenFiles?: string[];
	/* Default commands for the template */
	commands: Record<string, Command>;
}

export const DEFAULT_TEMPLATES: Record<string, Template> = {
	javascript: {
		name: "JavaScript",
		type: TemplateType.JavaScript,
		logo: DiNodejsSmall,
		commands: {
			run: {
				id: "ra6yccgnk7klp9jizhsk9h4e",
				name: "Run",
				command: "exec",
				args: ["index.js"],
			},
		},
		value:
			"AcoBmrnknQ0AJwECZnMBLwEnAJq55J0NAAR0eXBlAgQAmrnknQ0BCWRpcmVjdG9yeSEBAmZzDS9wYWNrYWdlLmpzb24BAAYhAQJmcwkvaW5kZXguanMBAAahmrnknQ0LAQAIoZq55J0NGQEADqGaueSdDSIBABChmrnknQ0xAQARoZq55J0NQgEAEqGaueSdDVQBABOhmrnknQ1nAQAUoZq55J0NewEAFaGaueSdDZABAQAWoZq55J0NpgEBABihmrnknQ29AQEAGaGaueSdDdYBAQAaoZq55J0N8AEBABuhmrnknQ2LAgEAHKGaueSdDacCAQAdoZq55J0NxAIBAB6hmrnknQ3iAgEAH6GaueSdDYEDAQAgoZq55J0NoQMBACGhmrnknQ3CAwEAIqGaueSdDeQDAQAjoZq55J0NhwQBACShmrnknQ2rBAEAKaGaueSdDdAEAQAroZq55J0N+gQBACyhmrnknQ2mBQEALaGaueSdDdMFAQAuoZq55J0NgQYBAC+hmrnknQ2wBgEAMKGaueSdDeAGAQAxoZq55J0NkQcBADKhmrnknQ3DBwEAM6GaueSdDfYHAQA0oZq55J0NqggBADahmrnknQ3fCAEARKGaueSdDZYJAQBGoZq55J0N2wkBAEehmrnknQ2iCgEASKGaueSdDeoKAQBJoZq55J0NswsBAEqhmrnknQ39CwEAS6GaueSdDcgMAQBMoZq55J0NlA0BAE2hmrnknQ3hDQEAT6GaueSdDa8OAQBQoZq55J0N/w4BAFGhmrnknQ3QDwEAUqGaueSdDaIQAQBToZq55J0N9RABAFShmrnknQ3JEQEAVaGaueSdDZ4SAQBWoZq55J0N9BIBAFehmrnknQ3LEwEAWKGaueSdDaMUAQBZoZq55J0N/BQBAFqhmrnknQ3WFQEAW6eaueSdDbEWAScAmrnknQ2NFwR0eXBlAgQAmrnknQ2OFwRmaWxlJwCaueSdDY0XCGNvbnRlbnRzAgQAmrnknQ2TF1Z7CiAgICAibmFtZSI6ICJydW56aXAtbm9kZSIsCiAgICAic2NyaXB0cyI6IHsKICAgICAgICAic3RhcnQiOiAibm9kZSBpbmRleC5qcyIKICAgIH0KfQABoZq55J0NEgEABwEAmrnknQ0YAaGaueSdDesXAQAIgZq55J0N8xcBoZq55J0N9BcBAAmBmrnknQ39FwGhmrnknQ3+FwEACoGaueSdDYgYAaGaueSdDYkYAQALgZq55J0NlBgBoZq55J0NlRgBAAyBmrnknQ2hGAGhmrnknQ2iGAEADYGaueSdDa8YAaGaueSdDbAYAQAOgZq55J0NvhgBoZq55J0NvxgBAA+BmrnknQ3OGAGhmrnknQ3PGAEAEIGaueSdDd8YAaGaueSdDeAYAQARgZq55J0N8RgCoZq55J0N8hgBABOBmrnknQ2FGQKhmrnknQ2GGQEAFYGaueSdDZsZAaGaueSdDZwZAQAWgZq55J0NshkBoZq55J0NsxkBABeBmrnknQ3KGQGhmrnknQ3LGQEAGIGaueSdDeMZAaGaueSdDeQZAQAZgZq55J0N/RkBoZq55J0N/hkBABqBmrnknQ2YGgGhmrnknQ2ZGgEAG4GaueSdDbQaAaGaueSdDbUaAQAcgZq55J0N0RoBoZq55J0N0hoBAB2BmrnknQ3vGgGhmrnknQ3wGgEAHoGaueSdDY4bAaGaueSdDY8bAQAfgZq55J0NrhsBoZq55J0NrxsBACCBmrnknQ3PGwGnmrnknQ3QGwEnAJq55J0N8hsEdHlwZQIEAJq55J0N8xsEZmlsZScAmrnknQ3yGwhjb250ZW50cwIEAJq55J0N+BsbY29uc29sZS5sb2coIkhlbGxvIHdvcmxkISIpAZq55J0NAguCF+oXiAQ=",
		defaultOpenFiles: ["/index.js"],
	},
	python: {
		name: "Python",
		type: TemplateType.Python,
		commands: {
			run: {
				id: "zg8iqi23uultl0yqmax4e9ct",
				name: "Run",
				command: "main.py",
				args: [],
			},
		},
		logo: DiPython,
		value:
			"BasBmrnknQ0AJwECZnMBLwEnAJq55J0NAAR0eXBlAgQAmrnknQ0BCWRpcmVjdG9yeSEBAmZzDS9wYWNrYWdlLmpzb24BAAYhAQJmcwkvaW5kZXguanMBAAahmrnknQ0LAQAIoZq55J0NGQEADqGaueSdDSIBABChmrnknQ0xAQARoZq55J0NQgEAEqGaueSdDVQBABOhmrnknQ1nAQAUoZq55J0NewEAFaGaueSdDZABAQAWoZq55J0NpgEBABihmrnknQ29AQEAGaGaueSdDdYBAQAaoZq55J0N8AEBABuhmrnknQ2LAgEAHKGaueSdDacCAQAdoZq55J0NxAIBAB6hmrnknQ3iAgEAH6GaueSdDYEDAQAgoZq55J0NoQMBACGhmrnknQ3CAwEAIqGaueSdDeQDAQAjoZq55J0NhwQBACShmrnknQ2rBAEAKaGaueSdDdAEAQAroZq55J0N+gQBACyhmrnknQ2mBQEALaGaueSdDdMFAQAuoZq55J0NgQYBAC+hmrnknQ2wBgEAMKGaueSdDeAGAQAxoZq55J0NkQcBADKhmrnknQ3DBwEAM6GaueSdDfYHAQA0oZq55J0NqggBADahmrnknQ3fCAEARKGaueSdDZYJAQBGoZq55J0N2wkBAEehmrnknQ2iCgEASKGaueSdDeoKAQBJoZq55J0NswsBAEqhmrnknQ39CwEAS6GaueSdDcgMAQBMoZq55J0NlA0BAE2hmrnknQ3hDQEAT6GaueSdDa8OAQBQoZq55J0N/w4BAFGhmrnknQ3QDwEAUqGaueSdDaIQAQBToZq55J0N9RABAFShmrnknQ3JEQEAVaGaueSdDZ4SAQBWoZq55J0N9BIBAFehmrnknQ3LEwEAWKGaueSdDaMUAQBZoZq55J0N/BQBAFqhmrnknQ3WFQEAW6GaueSdDbEWAQBdoZq55J0NEgEACKGaueSdDesXAQAJoZq55J0N9BcBAAqhmrnknQ3+FwEAC6GaueSdDYkYAQAMoZq55J0NlRgBAA2hmrnknQ2iGAEADqGaueSdDbAYAQAPoZq55J0NvxgBABChmrnknQ3PGAEAEaGaueSdDeAYAQAToZq55J0N8hgBABWhmrnknQ2GGQEAFqGaueSdDZwZAQAXoZq55J0NsxkBABihmrnknQ3LGQEAGaGaueSdDeQZAQAaoZq55J0N/hkBABuhmrnknQ2ZGgEAHKGaueSdDbUaAQAdoZq55J0N0hoBAB6hmrnknQ3wGgEAH6GaueSdDY8bAQAgoZq55J0NrxsBACGhmrnknQ3QGwEAIQyrtNXWCQAhAQJmcwEvAQAKJwECZnMIL21haW4ucHkBJwCrtNXWCQsEdHlwZQIEAKu01dYJDARmaWxlJwCrtNXWCQsIY29udGVudHMCBACrtNXWCREGcHJpbnQohKu01dYJFwEpxKu01dYJF6u01dYJGAEixKu01dYJGau01dYJGAEiwau01dYJGau01dYJGgfEq7TV1gkhq7TV1gkaDEhlbGxvIFdvcmxkIQLAhJWaBwAhAQJmcwEvAQAKAqXU6/kCACEBAmZzAS8BAAoC68eqYQAhAQJmcwEvAQAKBZq55J0NAQuJHKu01dYJAgALGwfAhJWaBwEAC6XU6/kCAQAL68eqYQEACw==",
	},
};
